import React, { FC, useMemo } from 'react';
import StringUtils from '../../shared/StringUtils';
import { useTranslation } from 'react-i18next';
import { Container, Typography, useTheme } from '@mui/material';

import HeaderImage from '../../assets/images/aianalysis/headerbild-01@2x.png';
import SideImage from '../../assets/images/aianalysis/bewerber.svg';
import FooterImage from '../../assets/images/aianalysis/tacho-zufriedenheit-1.png';
import IconDocuments from '../../assets/images/aianalysis/dokumente-hell-tuerkis.png';
import IconPerson from '../../assets/images/aianalysis/person-hell-tuerkis.png';

interface Props {
  reportData: string;
}

interface CategoryData {
  kategorie?: string;
  positiv?: string[];
  negativ?: string[];
}

interface ReasonsData {
  [key: string]: {
    grund?: string;
    prozent?: number;
  }[];
}

interface CountReportData {
  aussage?: string;
  anzahl?: string;
}
interface CountReport {
  positiv?: CountReportData[];
  negativ?: CountReportData[];
}

const AnalysisReportContent: FC<Props> = ({ reportData }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const parsedReport = useMemo(() => {
    if (!reportData) return null;

    try {
      return JSON.parse(reportData);
    } catch {
      const match = reportData.match(/\{[\s\S]*\}/);
      if (match) {
        try {
          return JSON.parse(match[0]);
        } catch {
          console.error('Found possible JSON but parsing failed');
        }
      }
      console.error('No valid JSON found in reportData');
      return null;
    }
  }, [reportData]);

  function isLikelyHtml(str?: string) {
    if (!str) return false;
    const htmlRegex = /<[a-z][\s\S]*>/i;
    return htmlRegex.test(str.trim());
  }

  const getReportType = () => {
    if (parsedReport) {
      if (parsedReport['kunden']) {
        return 'CUSTOMER';
      } else {
        return 'HR';
      }
    } else if (isLikelyHtml(reportData)) {
      return 'HTML';
    }
  };

  const renderReport = (
    titleKey: string,
    sections: {
      entityKey: string;
      labelKey: string;
    }[]
  ) => (
    <Container sx={{ display: 'flex', flexFlow: 'column', mx: 'auto' }}>
      <Typography
        sx={{ maxWidth: '60%', height: '160px', fontSize: '3rem' }}
        variant={'h1'}
      >
        {t(titleKey as any)}
      </Typography>
      {sections.map(({ entityKey, labelKey }, c) => (
        <>
          {renderReviewCategoriesTable(
            entityKey,
            parsedReport?.[entityKey]?.bewertungskategorien ?? [],
            c === 0,
            c === 1
          )}
        </>
      ))}

      <h2 style={{ margin: 'auto' }}>{t('statementCounts')}</h2>
      {sections.map(({ entityKey, labelKey }) => (
        <>
          <h3 style={{ margin: '35px auto 0 auto' }}>{t(labelKey as any)}</h3>
          <div style={{ display: 'flex', gap: '60px', justifyContent: 'center' }}>
            {renderCountTable(
              t('positiveStatements'),
              parsedReport?.[entityKey]?.aussagen_zaehlung?.positiv ?? []
            )}
            {renderCountTable(
              t('negativeStatements'),
              parsedReport?.[entityKey]?.aussagen_zaehlung?.negativ ?? []
            )}
          </div>
        </>
      ))}

      {/*
      <h1>{t('causesAnalysis')}</h1>
      {sections.map(({ entityKey }) =>
        renderReasonsTable(entityKey, parsedReport?.[entityKey]?.ursachen ?? {})
      )*/}

      <h2 style={{ margin: '60px auto 20px auto' }}>{t('improvementRecommendations')}</h2>
      <div style={{ display: 'flex', gap: '80px' }}>
        {sections.map(({ entityKey, labelKey }) => (
          <div
            style={{
              background: theme.palette.background.default,
              padding: '20px  40px 20px',
              flexGrow: 1,
              flexBasis: '50%',
              maxWidth: '50%',
              position: 'relative',
            }}
          >
            {entityKey === 'mitarbeiter' && (
              <img
                src={IconPerson}
                style={{ position: 'absolute', right: '20px', top: '20px' }}
              />
            )}
            {entityKey === 'bewerber' && (
              <img
                src={IconDocuments}
                style={{ position: 'absolute', right: '20px', top: '20px' }}
              />
            )}
            <h3>{t(labelKey as any)}</h3>
            <p>{parsedReport?.[entityKey]?.empfehlungen ?? ''}</p>
          </div>
        ))}
      </div>

      <h2 style={{ margin: '60px auto 20px auto' }}>{t('mood')}</h2>
      <div style={{ display: 'flex', gap: '80px' }}>
        {sections.map(({ entityKey, labelKey }) => (
          <div
            style={{
              background: theme.palette.background.default,
              padding: '20px  40px 20px',
              flexGrow: 1,
              flexBasis: '50%',
              maxWidth: '50%',
              position: 'relative',
            }}
          >
            {entityKey === 'mitarbeiter' && (
              <img
                src={IconPerson}
                style={{ position: 'absolute', right: '20px', top: '20px' }}
              />
            )}
            {entityKey === 'bewerber' && (
              <img
                src={IconDocuments}
                style={{ position: 'absolute', right: '20px', top: '20px' }}
              />
            )}
            <h3>{t(labelKey as any)}</h3>
            <p>{parsedReport?.[entityKey]?.stimmungsbild ?? ''}</p>
          </div>
        ))}
      </div>
      <img
        src={FooterImage}
        style={{ width: '50%', margin: '60px auto' }}
        alt={'AI Analysis Footer'}
      />
      <Typography sx={{ color: theme.palette.text.primary, fontSize: '0.8rem' }}>
        {t('ai_disclaimer_content')}
      </Typography>
    </Container>
  );
  const sanitizeHtml = (html: string) => {
    let sanitized = html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, ''); // Remove script tags
    sanitized = sanitized.replace(/javascript:/gi, ''); // Remove javascript: URLs
    return sanitized;
  };
  const renderHtmlReport = () => {
    let content = reportData;
    const htmlStart = '```html\n';
    if (content.startsWith(htmlStart)) {
      content = content.substring(htmlStart.length);
      content = content.replaceAll('```', '');
    }
    const regexb = /<body[^>]*>(.*?)<\/body>/is;
    const matchb = content.match(regexb);
    if (matchb && matchb[1] !== undefined) {
      content = matchb[1];
    }
    content = sanitizeHtml(content);

    return <div dangerouslySetInnerHTML={{ __html: content }}></div>;
  };

  const renderUnknown = () => (
    <>
      <h3>{t('error_parsing_ai_report')}</h3>
    </>
  );

  const renderReviewCategoriesTable = (
    categoryName: string,
    categoryData: CategoryData[],
    showHeaderImage: boolean = false,
    showSideImage: boolean = false
  ) =>
    categoryData?.length > 0 && (
      <>
        <div style={{ display: 'flex', gap: '20px', marginBottom: '40px' }}>
          {showSideImage && (
            <img
              src={SideImage}
              alt="AI Side Image"
              style={{
                bottom: 0,
                right: '0',
                width: '33%',
                height: 'auto',
              }}
            />
          )}
          <div style={{ width: '100%' }}>
            <div style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
              {showHeaderImage && (
                <img
                  src={HeaderImage}
                  alt="AI Header"
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    right: '0',
                    width: '40%',
                    height: 'auto',
                  }}
                />
              )}
              <h3>{t(categoryName as any)}</h3>
            </div>
            <table style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>{t('reviewCategory')}</th>
                  <th>{t('positiveAspect')}</th>
                  <th>{t('negativeAspect')}</th>
                </tr>
              </thead>
              <tbody>
                {categoryData.map((row, idx) => (
                  <tr key={idx}>
                    <td>{row.kategorie ?? ''}</td>
                    <td>{row.positiv?.join(', ') ?? ''}</td>
                    <td>{row.negativ?.join(', ') ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </>
    );

  const renderReasonsTable = (categoryName: string, reasonsData: ReasonsData) =>
    reasonsData &&
    Object.entries(reasonsData).map(([headline, reasons], idx) => (
      <div key={idx}>
        <h3>
          {StringUtils.toTitleCase(headline)} – {t('frequentReasons')}
        </h3>
        <table>
          <thead>
            <tr>
              <th>{t('reason')}</th>
              <th>{t('frequency')}</th>
            </tr>
          </thead>
          <tbody>
            {reasons.map((reason, i) => (
              <tr key={i}>
                <td>{reason.grund ?? ''}</td>
                <td>{reason.prozent ?? 0}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ));

  const renderCountTable = (categoryName: string, countData: CountReport[keyof CountReport]) => (
    <div style={{ width: '33%', minWidth: '375px' }}>
      <h3>{categoryName}</h3>
      <table style={{ width: '100%' }}>
        <thead>
          <tr>
            <th>{t('statement')}</th>
            <th>{t('count')}</th>
          </tr>
        </thead>
        <tbody>
          {countData?.map((entry, i) => (
            <tr key={i}>
              <td>{entry.aussage ?? ''}</td>
              <td>{entry.anzahl ?? ''}</td>
            </tr>
          )) ?? null}
        </tbody>
      </table>
    </div>
  );

  const renderContent = () => {
    switch (getReportType()) {
      case 'CUSTOMER':
        return renderReport('customerReportTitle', [
          { entityKey: 'kunden', labelKey: 'customers' },
          { entityKey: 'interessenten', labelKey: 'prospects' },
        ]);
      case 'HR':
        return renderReport('hrReportTitle', [
          { entityKey: 'mitarbeiter', labelKey: 'employees' },
          { entityKey: 'bewerber', labelKey: 'applicants' },
        ]);
      case 'HTML':
        return renderHtmlReport();
      default:
        return renderUnknown();
    }
  };

  return <div className={'ai-review generatedHTML'}>{renderContent()}</div>;
};

export default AnalysisReportContent;
